import { MapPin } from '@phosphor-icons/react/dist/ssr/MapPin';

/**
 * ActivityDot — THE presence dot ("active now"), one home. Rendered by the
 * subtitle's activity-only branch and by the Anon chat header's dot-only
 * variant (exchange-polish item 3) — a size/color change here reaches both.
 */
export const ActivityDot = ({ size = 7 }) => (
  <span
    style={{
      display: 'inline-block',
      width: size,
      height: size,
      borderRadius: '50%',
      backgroundColor: '#34C759',
      flexShrink: 0,
    }}
  />
);

/**
 * StatusSubtitle — shared location/activity status display
 *
 * Used by chat headers (12px), detail pages (14px), and People cards (location only).
 * Implements the priority chain from the People epic:
 *   1. Live location (green pin) — suppresses "Active now" (location implies online)
 *   2. Live location + non-"now" activity — both shown with separator
 *   3. Hometown (gray pin) — blocks activity entirely
 *   4. Activity only — green dot for "Active now", gray text otherwise
 *
 * @param {string} locationDisplay - Live location string (green pin)
 * @param {string} hometown - Static location (gray pin)
 * @param {{ text: string, isActive: boolean }} activityStatus - From formatActivityStatus()
 * @param {number} size - Font/icon size (12 or 14)
 * @param {boolean} showActivity - Whether to render activity (false for cards)
 * @param {string} weight - Font weight for the text (default '400'; '300' for a lighter look)
 */
const StatusSubtitle = ({
  locationDisplay = null,
  hometown = null,
  activityStatus = null,
  size = 14,
  showActivity = true,
  weight = '400',
}) => {
  const hasLocation = !!locationDisplay;
  const hasHometown = !!hometown;
  const hasActivity = showActivity && !!activityStatus;
  const isActiveNow = activityStatus?.isActive;

  // Nothing to show
  if (!hasLocation && !hasHometown && !hasActivity) return null;

  // Green dot size scales with text
  const dotSize = size === 12 ? 6 : 7;

  return (
    <span
      style={{
        fontSize: `${size}px`,
        fontWeight: weight,
        lineHeight: '1.2',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'flex',
        alignItems: 'center',
        gap: '3px',
      }}
    >
      {/* Live location (green pin + text) */}
      {hasLocation && (
        <>
          <MapPin size={size} color="#34C759" weight="fill" />
          <span style={{ color: '#34C759' }}>{locationDisplay}</span>
        </>
      )}

      {/* Location + activity (not "Active now" — location implies online) */}
      {hasLocation && hasActivity && !isActiveNow && (
        <>
          <span style={{ color: '#8E8E93', margin: '0 1px' }}> · </span>
          <span style={{ color: '#8E8E93' }}>{activityStatus.text}</span>
        </>
      )}

      {/* Hometown (gray pin) — blocks activity */}
      {!hasLocation && hasHometown && (
        <>
          <MapPin size={size} color="#8E8E93" weight="fill" />
          <span style={{ color: '#8E8E93' }}>{hometown}</span>
        </>
      )}

      {/* Activity only (no location, no hometown) */}
      {!hasLocation && !hasHometown && hasActivity && (
        <>
          {isActiveNow && <ActivityDot size={dotSize} />}
          <span style={{ color: isActiveNow ? '#34C759' : '#8E8E93' }}>{activityStatus.text}</span>
        </>
      )}
    </span>
  );
};

export default StatusSubtitle;
